import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Location, LangCode } from "../data/types.js";
import { t } from "../i18n/strings.js";
import { PhotoCarousel } from "./PhotoCarousel.js";
import { PhotoLightbox } from "./PhotoLightbox.js";
import { shareLocation } from "../lib/share.js";

interface Props {
  location: Location | null;
  onClose: () => void;
  lang: LangCode;
}

export function LocationSheet({ location, onClose, lang }: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [shareState, setShareState] = useState<"idle" | "copied">("idle");
  const [tipExpanded, setTipExpanded] = useState(false);

  const openLightbox = useCallback((i: number) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  }, []);

  const handleShare = useCallback(async () => {
    if (!location) return;
    const r = await shareLocation(location);
    if (r === "copied") {
      setShareState("copied");
      setTimeout(() => setShareState("idle"), 2000);
    }
  }, [location]);

  return (
    <AnimatePresence>
      {location && (
        <>
          <motion.div
            className="fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ background: "rgba(0,0,0,0.15)" }}
          />
          <motion.div
            className="fixed inset-0 z-50 bg-white flex flex-col"
            style={{
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 350, damping: 35 }}
          >
            <div className="flex flex-col flex-1 min-h-0">
              {/* Header */}
              <div className="flex items-center gap-3 px-5 pt-3 pb-3">
                <span
                  className="flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: "#B76E6E" }}
                >
                  {location.number}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-lg font-semibold text-ink-0 font-display truncate">
                      {location.name}
                    </h2>
                    <span className="text-sm text-ink-2 flex-shrink-0">
                      {location.nameJa}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-[10px] font-medium text-amber-700">
                      {t("badge.day", lang)}
                    </span>
                    {location.allowNight && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-[10px] font-medium text-indigo-700">
                        {t("badge.night", lang)}
                      </span>
                    )}
                    {location.allowSakura && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pink-50 text-[10px] font-medium text-pink-700">
                        {t("badge.sakura", lang)}
                      </span>
                    )}
                    {location.allowMomiji && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-[10px] font-medium text-orange-700">
                        {t("badge.momiji", lang)}
                      </span>
                    )}
                    {location.allowHot && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-[10px] font-medium text-red-700">
                        {t("badge.hot", lang)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-1 text-ink-1 hover:bg-surface-2 transition-colors text-sm flex-shrink-0"
                >
                  ✕
                </button>
              </div>

              <PhotoCarousel
                photos={location.photos || []}
                onPhotoClick={openLightbox}
              />

              {/* Photo tip — collapsed by default so the carousel keeps its
                  full visual weight. Tap the pill to reveal the tip inline. */}
              {location.tip && (
                <div className="px-5 pt-2 pb-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setTipExpanded((v) => !v)}
                    aria-expanded={tipExpanded}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-1 text-ink-1 text-[11px] font-medium active:scale-[0.97] transition-all"
                  >
                    <span aria-hidden="true">💡</span>
                    <span>{t("tip.label", lang)}</span>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`transition-transform ${tipExpanded ? "rotate-180" : ""}`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <AnimatePresence initial={false}>
                    {tipExpanded && (
                      <motion.div
                        key="tip-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-2 space-y-1">
                          {(
                            (lang !== "vi" &&
                              location.tipTranslations?.[lang]) ||
                            location.tip
                          )
                            .split("\n")
                            .filter(Boolean)
                            .map((line, i) => (
                              <p
                                key={i}
                                className="text-xs text-ink-2 italic leading-relaxed"
                              >
                                {line}
                              </p>
                            ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Action buttons */}
              <div className="px-5 py-3 flex-shrink-0 space-y-2">
                <div className="flex gap-2">
                  <a
                    href={location.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-sm font-medium transition-all active:scale-[0.98] border-ink-3/20 text-ink-1"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {t("btn.maps", lang)}
                  </a>
                  <button
                    onClick={handleShare}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-sm font-medium transition-all active:scale-[0.98] border-ink-3/20 text-ink-1"
                  >
                    {shareState === "copied" ? (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {t("btn.copied", lang)}
                      </>
                    ) : (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                        {t("btn.share", lang)}
                      </>
                    )}
                  </button>
                </div>
                <a
                  href="https://kimonorentalann.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center py-3 rounded-2xl text-white transition-all active:scale-[0.98]"
                  style={{ backgroundColor: "#B76E6E" }}
                >
                  <span className="text-[11px] text-white/70 font-normal">
                    {t("cta.subtitle", lang)}
                  </span>
                  <span className="text-sm font-semibold mt-0.5">
                    {t("cta.book", lang)}
                  </span>
                </a>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {lightboxOpen && (location.photos || []).length > 0 && (
              <PhotoLightbox
                photos={location.photos || []}
                initialIndex={lightboxIndex}
                onClose={() => setLightboxOpen(false)}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
